/** @jsxImportSource theme-ui @jsxRuntime classic */
import { useState, useEffect, useCallback } from 'react';

/**
 * Calculates the height of sticky/fixed headers in the document
 */
export const getStickyHeaderHeight = (): number => {
  if (typeof document === 'undefined') return 0;

  let calculatedHeight = 0;

  const elements = document.querySelectorAll(
    '[data-section="SecondaryMenuBar"]',
  );

  elements.forEach((el) => {
    if (el.hasAttribute('data-section')) {
      const elRect = el.getBoundingClientRect();
      calculatedHeight = Math.max(calculatedHeight, elRect.height);
    } else {
      const offsetValue = parseFloat(
        el.getAttribute('data-scroll-offset') || '0',
      );
      if (!isNaN(offsetValue) && offsetValue > 0) {
        calculatedHeight = Math.max(calculatedHeight, offsetValue);
      }
    }
  });

  return calculatedHeight;
};

/**
 * Hook to detect and measure sticky/fixed headers in the document
 *
 * @returns The calculated height of sticky/fixed headers at the top of the viewport
 */
export function useHeaderHeight(): number {
  const [headerHeight, setHeaderHeight] = useState(0);

  const updateHeaderHeight = useCallback(() => {
    const newHeight = getStickyHeaderHeight();
    setHeaderHeight(newHeight);
  }, []);

  useEffect(() => {
    updateHeaderHeight();

    const debouncedUpdate = debounce(updateHeaderHeight, 100);

    window.addEventListener('resize', debouncedUpdate, { passive: true });
    window.addEventListener('scroll', debouncedUpdate, { passive: true });

    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      window.removeEventListener('scroll', debouncedUpdate);
    };
  }, [updateHeaderHeight]);

  return headerHeight;
}

// Utility function for debouncing
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debounce(func: (...args: any[]) => void, wait: number) {
  let timeout: number | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function executedFunction(...args: any[]) {
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = window.setTimeout(() => {
      func(...args);
    }, wait);
  };
}
